import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  forwardRef,
} from 'react';

import { ClearButton } from 'components/Button';
import { Input } from 'components/Input/Input';
import {
  InputPrefixVariant,
  InputStyleVariant,
} from 'components/Input/Input.styled';
import { InputIcon } from 'components/Input/InputIcon';
import { InputIconContainer } from 'components/LocationInput/InputIconContainer';
import { TooltipStandalone } from 'components/TooltipStandalone';

import { LocationInputWrapper } from './LocationInputWrapper';

type Props = {
  autoComplete?: string;
  showLocationIcon: boolean;
  onClear: (() => void) | null | undefined;
  className?: string;
  disabled?: boolean;
  gMapsErrored: boolean;
  'data-qa-id': string;
  hasSuccess?: boolean;
  type?: string;
  value: string;
  id?: string;
  placeholder?: string;
  title?: string;
  'aria-autocomplete'?: 'none' | 'inline' | 'list' | 'both' | undefined;
  'aria-controls'?: string;
  'aria-activedescendant'?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
  prefixVariant?: InputPrefixVariant;
  styleVariant?: InputStyleVariant;
};

export const StyledInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      autoComplete,
      showLocationIcon,
      onClear,
      className,
      disabled,
      gMapsErrored,
      id,
      onChange,
      onBlur,
      onKeyDown,
      onKeyPress,
      onFocus,
      placeholder,
      prefixVariant,
      styleVariant,
      title,
      type,
      value,
      ...inputProps
    }: Props,
    ref
  ) => (
    <LocationInputWrapper className={className}>
      <Input
        fullHeight
        fullWidth
        inputProps={{
          ref,
          qaId: inputProps['data-qa-id'],
          disabled,
          type,
          value,
          autoComplete,
          ariaAutocomplete: inputProps['aria-autocomplete'],
          ariaControls: inputProps['aria-controls'],
          ariaActiveDescendant: inputProps['aria-activedescendant'],
          id,
          placeholder,
          title,
          onChange,
          onBlur,
          onFocus,
          onKeyDown,
          onKeyPress,
        }}
        prefix={
          showLocationIcon ? (
            <InputIconContainer>
              <InputIcon
                iconColor="black"
                iconName="location"
                size={24}
                noPaddingRight
              />
            </InputIconContainer>
          ) : null
        }
        prefixVariant={prefixVariant}
        styleVariant={styleVariant}
        suffix={
          <ClearButton
            $visible={onClear}
            onClick={onClear}
            data-qa-id={
              onClear ? `${inputProps['data-qa-id']}-clear` : undefined
            }
          />
        }
      />

      {gMapsErrored && (
        <TooltipStandalone>
          {getText(
            'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.'
          )}
        </TooltipStandalone>
      )}
    </LocationInputWrapper>
  )
);
